import { get, set, merge, omit } from 'lodash-es';
import { EdmundsAPI } from 'client/data/api/api-client';
import { addFinancing } from 'client/actions/financing';
import { logger } from 'client/utils/logger/logger';
import { ClientConfig } from 'client/configuration';
import { PREQUALIFY_API, PREQUALIFY_API_DEV, WARN } from 'site-modules/financing/constants/financing';
import { ERROR_STATUS, FINANCING_RESPONSE_TYPES } from 'site-modules/shared/constants/financing/financing';
import { getTaxesFeesApiUrl } from 'site-modules/shared/utils/financing/api';
import {
  getCustomerLookupAttributes,
  getFormattedOfferData,
  getPrequalifyData,
  isPreviousPreQualNotFoundError,
  getErrorStatus,
} from 'site-modules/financing/utils/financing-utils';
import { getDecryptedFields } from 'site-modules/financing/utils/encrypting-utils';
import { getDefaultApiHeaders } from 'site-modules/shared/utils/car-buying/api-helper';
import { awsServiceAccountSignedOptions } from 'client/site-modules/shared/utils/financing/aws-service-account-signed-options';
import { shouldMockResponse, sendMockResponse } from 'site-modules/shared/utils/financing/mock-util';
import { getConfigOptions, getMetricHeaders, geApiOptions } from 'site-modules/financing/utils/api-utils';

async function getOffers({ store, applicationId, offerData }) {
  const state = store.getState();
  const { CapitalOneAPI, isDisableAWSAuth, isAllowBypassApiGateway } = getConfigOptions(state);

  try {
    let response;
    let options;
    let signedOptions;
    const path = `${ClientConfig.get('carBuying').capOneAPI.path}${PREQUALIFY_API.PRICING}${applicationId}`;

    if (!isDisableAWSAuth && !isAllowBypassApiGateway) {
      const awsOptions = await awsServiceAccountSignedOptions(offerData, path);
      options = get(awsOptions, 'options');
      signedOptions = get(awsOptions, 'signedOptions');
    }

    try {
      if (isDisableAWSAuth || isAllowBypassApiGateway) {
        response = await CapitalOneAPI.fetchJson(`${PREQUALIFY_API_DEV.PRICING}${applicationId}`, {
          ...geApiOptions(state),
          body: JSON.stringify(offerData),
        });
      } else {
        const visitorId = get(state, 'visitor.id');
        const optionsWithVisitorId = {
          headers: { ...getDefaultApiHeaders(visitorId, true), ...getMetricHeaders(state) },
        };
        response = await CapitalOneAPI.fetchJson(path, merge(signedOptions, optionsWithVisitorId, { retries: 0 }));
      }

      return { response, status: ERROR_STATUS.APPROVED };
    } catch (e) {
      const { status } = getErrorStatus(e);
      logger[WARN](
        isDisableAWSAuth
          ? `Get offers error is - ${e}.`
          : `Get offers error is - ${e}. Vehicle pricing polling request params: ${JSON.stringify(
              {
                options: omit(options, 'body'),
                signedOptions: omit(signedOptions, 'body'),
              },
              null,
              2
            )}`
      );
      return { response: e, status };
    }
  } catch (e) {
    logger.error(e, 'Not running in ECS container. No AWS credentials available.');

    return { response: e, status: ERROR_STATUS.SYSTEM_ERROR };
  }
}
/**
 * Set Financing Response
 * @param {Object} postData
 * @param {Object} store
 * @returns {Promise}
 */
export async function postFinancing(postData, store) {
  if (shouldMockResponse(postData)) {
    return sendMockResponse({
      type: FINANCING_RESPONSE_TYPES.PREQUALIFY,
      store,
    });
  }

  const data = getPrequalifyData(postData);
  const state = store.getState();
  const isCapOne = get(postData, 'isCapOne') === 'true';
  const isPreVinFlow = get(postData, 'isPreVinFlow') === 'true';
  const { CapitalOneAPI, isAllowBypassApiGateway, isDisableAWSAuth, baseUrl } = getConfigOptions(state);

  try {
    let response;
    let options;
    let signedOptions;
    const path = `${ClientConfig.get('carBuying').capOneAPI.path}${PREQUALIFY_API.OFFERS_POLLING}`;

    if (!isDisableAWSAuth && !isAllowBypassApiGateway) {
      const awsOptions = await awsServiceAccountSignedOptions(data, path);
      options = get(awsOptions, 'options');
      signedOptions = get(awsOptions, 'signedOptions');
    }

    try {
      if (isDisableAWSAuth || isAllowBypassApiGateway) {
        response = await CapitalOneAPI.fetchJson(PREQUALIFY_API_DEV.OFFERS_POLLING, {
          ...geApiOptions(state),
          body: JSON.stringify(data),
        });
      } else {
        const visitorId = get(state, 'visitor.id');
        const optionsWithVisitorId = {
          headers: { ...getDefaultApiHeaders(visitorId, true), ...getMetricHeaders(state) },
        };

        response = await CapitalOneAPI.fetchJson(path, merge(signedOptions, optionsWithVisitorId, { retries: 0 }));
      }

      const decisionStatus = get(response, 'offers[0].decision.decisionStatus');

      if (decisionStatus === ERROR_STATUS.APPROVED) {
        const applicationId = get(response, 'prequalificationApplicationId');

        if (isCapOne && !isPreVinFlow) {
          const calculatorSelections = JSON.parse(get(postData, 'calculatorSelections')) || {};
          const lenderId = get(response, 'offers[0].lenderId');

          const offerData = getFormattedOfferData({
            data: postData,
            calculatorSelections,
            lenderId,
          });

          const offersResponse = await getOffers({
            store,
            applicationId,
            offerData,
          });

          store.dispatch(
            addFinancing({
              offers: get(offersResponse, 'response.offers'),
              status: get(offersResponse, 'status'),
              applicationId,
              type: FINANCING_RESPONSE_TYPES.PREQUALIFY,
              response: [response, get(offersResponse, 'response')],
              payload: [data, offerData],
              api: [`${baseUrl}${PREQUALIFY_API_DEV.OFFERS_POLLING}`, `${baseUrl}${PREQUALIFY_API_DEV.PRICING}`],
            })
          );
        } else {
          store.dispatch(
            addFinancing({
              status: decisionStatus,
              type: FINANCING_RESPONSE_TYPES.PREQUALIFY,
              applicationId,
              response: [response],
              payload: [data],
              api: [`${baseUrl}${PREQUALIFY_API_DEV.OFFERS_POLLING}`],
            })
          );
        }
      } else {
        store.dispatch(
          addFinancing({
            status: decisionStatus || ERROR_STATUS.SYSTEM_ERROR,
            type: FINANCING_RESPONSE_TYPES.PREQUALIFY,
            response: [response],
            payload: [data],
            api: [`${baseUrl}${PREQUALIFY_API_DEV.OFFERS_POLLING}`],
          })
        );
      }
    } catch (e) {
      store.dispatch(
        addFinancing({
          status: ERROR_STATUS.SYSTEM_ERROR,
          errors: [{ isError: true, message: e.message }],
          type: FINANCING_RESPONSE_TYPES.PREQUALIFY,
          response: [e],
          payload: [data],
          api: [`${baseUrl}${PREQUALIFY_API_DEV.OFFERS_POLLING}`],
        })
      );

      logger[WARN](
        e,
        isDisableAWSAuth
          ? `Prequalify error is - ${e}.`
          : `Prequalify error is - ${e}. Offers polling API request params: ${JSON.stringify(
              {
                options: omit(options, 'body'),
                signedOptions: omit(options, 'body'),
              },
              null,
              2
            )}`
      );
    }
  } catch (e) {
    store.dispatch(
      addFinancing({
        status: ERROR_STATUS.SYSTEM_ERROR,
        errors: [{ isError: true, message: e.message }],
        type: FINANCING_RESPONSE_TYPES.PREQUALIFY,
        response: [e],
        payload: [data],
        api: [`${baseUrl}${PREQUALIFY_API_DEV.OFFERS_POLLING}`],
      })
    );

    logger.error(e, 'Not running in ECS container. No AWS credentials available.');
  }

  return null;
}
/**
 * Set Financing Response
 * @param {Object} postData
 * @param {Object} store
 * @returns {Promise}
 */
export async function postFinancingExistingPrequalify(postData, store) {
  if (shouldMockResponse(postData)) {
    return sendMockResponse({
      type: FINANCING_RESPONSE_TYPES.EXISTING_PREQUALIFY,
      store,
    });
  }

  const data = getCustomerLookupAttributes(postData);
  const state = store.getState();
  const isCapOne = get(postData, 'isCapOne') === 'true';
  const isPreVinFlow = get(postData, 'isPreVinFlow') === 'true';
  const { CapitalOneAPI, isAllowBypassApiGateway, isDisableAWSAuth, baseUrl } = getConfigOptions(state);

  try {
    let response;
    let options;
    let signedOptions;
    const path = `${ClientConfig.get('carBuying').capOneAPI.path}${PREQUALIFY_API.PREQUALIFY}`;

    if (!isDisableAWSAuth && !isAllowBypassApiGateway) {
      const awsOptions = await awsServiceAccountSignedOptions(data, path);

      options = get(awsOptions, 'options');
      signedOptions = get(awsOptions, 'signedOptions');
    }

    try {
      if (isDisableAWSAuth || isAllowBypassApiGateway) {
        response = await CapitalOneAPI.fetchJson(PREQUALIFY_API_DEV.PREQUALIFY, {
          ...geApiOptions(state),
          body: JSON.stringify(data),
        });
      } else {
        const visitorId = get(state, 'visitor.id');
        const optionsWithVisitorId = {
          headers: { ...getDefaultApiHeaders(visitorId, true), ...getMetricHeaders(state) },
        };
        response = await CapitalOneAPI.fetchJson(path, merge(signedOptions, optionsWithVisitorId));
      }

      const firstApplication = get(response, 'applications[0]');
      const isExpired =
        Date.now() > get(firstApplication, 'offers[0].offerLifecycleDates.offerExpirationTimestamp') * 1000;

      if (isExpired) {
        store.dispatch(
          addFinancing({
            status: ERROR_STATUS.EXPIRED,
            type: FINANCING_RESPONSE_TYPES.EXISTING_PREQUALIFY,
            response: [response],
            payload: [data],
            api: [`${baseUrl}${PREQUALIFY_API_DEV.PREQUALIFY}`],
          })
        );
      } else {
        const applicationId = get(firstApplication, 'id');
        if (isCapOne && !isPreVinFlow) {
          const calculatorSelections = JSON.parse(get(postData, 'calculatorSelections', ''));
          const lenderId = get(firstApplication, 'offers[0].lenderId');
          const offerData = getFormattedOfferData({
            data: postData,
            calculatorSelections,
            lenderId,
          });
          const offersResponse = await getOffers({
            store,
            applicationId,
            offerData,
          });

          store.dispatch(
            addFinancing({
              offers: get(offersResponse, 'response.offers'),
              status: get(offersResponse, 'status'),
              applicationId,
              type: FINANCING_RESPONSE_TYPES.EXISTING_PREQUALIFY,
              response: [response, get(offersResponse, 'response')],
              payload: [data, offerData],
              api: [`${baseUrl}${PREQUALIFY_API_DEV.PREQUALIFY}`, `${baseUrl}${PREQUALIFY_API_DEV.PRICING}`],
            })
          );
        } else {
          store.dispatch(
            addFinancing({
              status: ERROR_STATUS.APPROVED,
              type: FINANCING_RESPONSE_TYPES.EXISTING_PREQUALIFY,
              applicationId,
              response: [response],
              payload: [data],
              api: [`${baseUrl}${PREQUALIFY_API_DEV.PREQUALIFY}`],
            })
          );
        }
      }
    } catch (e) {
      const status = isPreviousPreQualNotFoundError(e) ? ERROR_STATUS.PREQUAL_NOT_FOUND : ERROR_STATUS.SYSTEM_ERROR;
      store.dispatch(
        addFinancing({
          status,
          errors: [{ isError: true, message: e.message }],
          type: FINANCING_RESPONSE_TYPES.EXISTING_PREQUALIFY,
          response: [e],
          payload: [data],
          api: [`${baseUrl}${PREQUALIFY_API_DEV.PREQUALIFY}`],
        })
      );

      logger[WARN](
        isDisableAWSAuth
          ? `Search error is - ${e}.`
          : `Search error is - ${e}. Search API request params: ${JSON.stringify(
              {
                options: omit(options, 'body'),
                signedOptions: omit(signedOptions, 'body'),
              },
              null,
              2
            )}`
      );
    }
  } catch (e) {
    store.dispatch(
      addFinancing({
        status: ERROR_STATUS.SYSTEM_ERROR,
        errors: [{ isError: true, message: e.message }],
        type: FINANCING_RESPONSE_TYPES.EXISTING_PREQUALIFY,
        response: [e],
        payload: [data],
        api: [`${baseUrl}${PREQUALIFY_API_DEV.PREQUALIFY}`],
      })
    );

    logger.error(e, 'Not running in ECS container. No AWS credentials available.');
  }

  return null;
}

/**
 * Set Financing Offers Response
 * @param {Object} postData
 * @param {Object} store
 * @returns {Promise}
 */
export async function postFinancingOffers(postData, store) {
  const calculatorSelections = JSON.parse(get(postData, 'calculatorSelections', ''));
  const prequalificationData = JSON.parse(get(postData, 'prequalificationData', ''));
  const decryptedPrequalifyData = await getDecryptedFields(prequalificationData);
  const applicationId = get(decryptedPrequalifyData, 'id');
  const { zipCode, displayPrice, tradeIn = 0, tradeInOwedAmount = 0 } = calculatorSelections;
  const { styleId } = postData;
  let taxesAndFeesResponse = {};

  if (zipCode && styleId) {
    const { docFee = 0, marketValue } = postData;

    try {
      taxesAndFeesResponse = await EdmundsAPI.fetchJson(
        getTaxesFeesApiUrl({
          zipCode,
          styleId,
          tradeIn: tradeIn - tradeInOwedAmount,
          salesPrice: displayPrice,
          marketValue,
        })
      );
      set(taxesAndFeesResponse, 'fees.docFee', Number.parseFloat(docFee));
      set(taxesAndFeesResponse, 'zipCode', zipCode);
    } catch (e) {
      logger.error(e, `Not able to get taxes and fees. Error - ${e}`);
    }
  }

  const offerData = getFormattedOfferData({
    data: postData,
    calculatorSelections,
    lenderId: get(postData, 'lenderId'),
    taxesAndFees: taxesAndFeesResponse,
  });
  const state = store.getState();
  const { baseUrl } = getConfigOptions(state);

  const offersResponse = await getOffers({
    store,
    applicationId,
    offerData,
  });

  const offers = get(offersResponse, 'response.offers', []);
  offers.forEach(offer => set(offer, 'vehicleStructure.taxesAndFees', taxesAndFeesResponse));

  store.dispatch(
    addFinancing({
      offers: get(offersResponse, 'response.offers'),
      status: get(offersResponse, 'status'),
      applicationId,
      type: FINANCING_RESPONSE_TYPES.UPDATE,
      response: [get(offersResponse, 'response')],
      payload: [offerData],
      api: [`${baseUrl}${PREQUALIFY_API_DEV.PRICING}`],
    })
  );

  return null;
}
